import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { Phone, MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock, CheckCircle, Factory } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const LimpezaGalpaoIndustrial = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Vi o artigo sobre limpeza de galpão industrial e gostaria de um orçamento.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/limpeza-galpao-industrial'].title}
        description={seoData['/blog/limpeza-galpao-industrial'].description}
        canonical={seoData['/blog/limpeza-galpao-industrial'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-blue-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors">
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Blog</button>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button 
            onClick={() => navigate('/blog')}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Limpeza Pós-Obra de Galpão Industrial: Equipamentos e Técnicas Profissionais
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>9 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              A <strong>limpeza pós-obra de galpão industrial</strong> exige equipamentos profissionais e técnicas especializadas devido ao tamanho das áreas e ao tipo de sujeira acumulada. Veja como realizar esse serviço com eficiência.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Por Que Galpões Industriais Exigem Limpeza Especializada?
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">Desafios Específicos:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <Factory className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Grandes áreas:</strong> Galpões com 500m² a 5.000m² ou mais</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Factory className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Sujeira pesada:</strong> Cimento, argamassa, óleo, graxa, poeira industrial</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Factory className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Pisos especiais:</strong> Concreto polido, epóxi, piso industrial</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Factory className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700"><strong>Prazos apertados:</strong> Necessidade de entregar rapidamente para operação</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Equipamentos Essenciais para Limpeza de Galpão Industrial
            </h2>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              1. Lavadoras de Piso Industriais
            </h3>
            <p className="text-gray-700 mb-4">
              Para grandes áreas, lavadoras tripuladas são essenciais:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Lavadoras a bateria:</strong> Silenciosas, sem emissão de gases (ideais para áreas fechadas)</li>
              <li><strong>Lavadoras a GLP:</strong> Maior autonomia, ideais para áreas muito grandes</li>
              <li><strong>Capacidade:</strong> Limpam de 2.000 a 5.000 m² por hora</li>
              <li><strong>Resultado:</strong> Limpeza profunda com recuperação de água (sem deixar poças)</li>
            </ul>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              2. Varredeiras Industriais
            </h3>
            <p className="text-gray-700 mb-6">
              Ideais para remoção de entulho fino, pó e resíduos secos antes da lavagem. Varredeiras industriais podem cobrir até 10.000 m² por hora.
            </p>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mt-8 mb-4">
              3. Aspiradores Industriais
            </h3>
            <p className="text-gray-700 mb-6">
              Para pó fino acumulado em cantos, estruturas metálicas e áreas de difícil acesso.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Processo de Limpeza Pós-Obra em Galpão Industrial
            </h2>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-4">Passo a Passo:</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Etapa 1: Remoção de Entulho</h4>
                    <p className="text-gray-700">Retirada de restos de madeira, plásticos, papelão e entulho grosso.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Etapa 2: Varredura Industrial</h4>
                    <p className="text-gray-700">Uso de varredeira para remover pó, terra e resíduos finos do piso.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Etapa 3: Remoção de Manchas Pesadas</h4>
                    <p className="text-gray-700">Aplicação de produtos específicos para remover cimento, argamassa, óleo e graxa.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Etapa 4: Lavagem Profissional</h4>
                    <p className="text-gray-700">Lavadora industrial com sistema de recuperação de água limpa completamente o piso.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Etapa 5: Limpeza de Áreas Específicas</h4>
                    <p className="text-gray-700">Banheiros, vestiários, escritórios administrativos e áreas de apoio.</p>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Etapa 6: Conferência Final</h4>
                    <p className="text-gray-700">Vistoria completa e retrabalho em pontos necessários.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tipos de Pisos Industriais e Cuidados
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Tipo de Piso</th>
                    <th className="p-4 text-left">Cuidados Especiais</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Concreto Polido</td>
                    <td className="p-4">Evitar produtos ácidos; usar detergentes neutros</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Piso Epóxi</td>
                    <td className="p-4">Não usar escovas agressivas; secar bem após lavagem</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Piso Industrial</td>
                    <td className="p-4">Resistente; permite produtos mais fortes</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Concreto Cru</td>
                    <td className="p-4">Pode exigir impermeabilização após limpeza</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Quanto Tempo Leva?
            </h2>
            <p className="text-gray-700 mb-4">
              O tempo varia conforme o tamanho e nível de sujeira:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>500m²:</strong> 1 dia (equipe com equipamentos profissionais)</li>
              <li><strong>1.000m²:</strong> 1-2 dias</li>
              <li><strong>2.000m²:</strong> 2-3 dias</li>
              <li><strong>5.000m² ou mais:</strong> 3-5 dias</li>
            </ul>

            <Card className="bg-yellow-50 border-2 border-yellow-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">💡 Dica Profissional</h3>
                <p className="text-gray-700">
                  Para galpões que precisam entrar em operação rapidamente, é possível fazer limpeza por setores, liberando áreas gradualmente para início das atividades.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Custo Médio para Limpeza de Galpão Industrial
            </h2>
            <p className="text-gray-700 mb-4">
              Valores estimados para BH e região (2025):
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Galpão 500m²: R$ 2.500 - R$ 4.000</li>
              <li>Galpão 1.000m²: R$ 4.000 - R$ 7.000</li>
              <li>Galpão 2.000m²: R$ 7.000 - R$ 12.000</li>
              <li>Acima de 5.000m²: Orçamento específico (R$ 3-6 por m²)</li>
            </ul>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Precisa de Limpeza em Galpão Industrial?</h3>
                <p className="mb-6">
                  A WellTec possui equipamentos industriais e equipe especializada para limpar galpões em BH e região
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento para Galpão
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Artigos Relacionados</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/equipamentos-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Equipamentos Profissionais para Limpeza Pós-Obra</h4>
                  <p className="text-gray-600 text-sm">Conheça os principais equipamentos utilizados</p>
                </CardContent>
              </Card>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/blog/custo-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <h4 className="font-bold text-gray-900 mb-2">Quanto Custa Limpeza Pós-Obra?</h4>
                  <p className="text-gray-600 text-sm">Guia completo de preços para 2025</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="font-bold mb-4">WellTec</h3>
              <p className="text-gray-400 text-sm">Limpeza pós-obra profissional em BH e região</p>
            </div>
            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400">Locação de Equipamentos</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><a href="tel:+5531971823249" className="hover:text-green-400">(31) 97182-3249</a></li>
                <li><button onClick={handleWhatsAppClick} className="hover:text-green-400">WhatsApp</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button 
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default LimpezaGalpaoIndustrial;
